import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FiX } from "react-icons/fi";
import { getUpgradePricing, ServicePriceOption } from "../../../actions/stay-actions";
import { checkout, CheckoutResponse } from "../../../actions/order-actions";
import { useSnack } from "../../../hooks/useSnack";
import SnackbarComponent from "../../../components/Snackbar/Snackbar";
import StripeCheckout from "../../../components/Stripe/StripeCheckout";
import confetti from "canvas-confetti";
import "./ServiceDetail.scss";

// Default placeholder image
const PLACEHOLDER_IMAGE = "https://images.unsplash.com/photo-1584132967334-10e028bd69f7?w=1200&q=80";

function ServiceDetail() {
    const { serviceCode } = useParams<{ serviceCode: string }>();
    const navigate = useNavigate();
    const snack = useSnack();

    const [service, setService] = useState<ServicePriceOption | null>(null);
    const [currency, setCurrency] = useState<string>("USD");
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isCheckingOut, setIsCheckingOut] = useState(false);
    const [checkoutData, setCheckoutData] = useState<CheckoutResponse | null>(null);
    const [showStripeModal, setShowStripeModal] = useState(false);

    useEffect(() => {
        const fetchService = async () => {
            if (!serviceCode) {
                setError("Service not found");
                setIsLoading(false);
                return;
            }

            try {
                const response = await getUpgradePricing();
                const foundService = response.data.servicePrices?.find(
                    (s) => s.code === serviceCode
                );
                if (foundService) {
                    setService(foundService);
                    setCurrency(response.data.currency || "USD");
                } else {
                    setError("Service not found");
                }
            } catch (err: any) {
                setError(err.response?.data || "Failed to load service");
            } finally {
                setIsLoading(false);
            }
        };

        fetchService();
    }, [serviceCode]);

    const handleCheckout = async () => {
        if (!service) return;

        setIsCheckingOut(true);
        try {
            const response = await checkout({ serviceCode: service.code });
            setCheckoutData(response.data);
            setShowStripeModal(true);
        } catch (err: any) {
            snack.setMessage(err.response?.data || "Failed to start checkout");
            snack.setSeverity("error");
            snack.onOpen();
        } finally {
            setIsCheckingOut(false);
        }
    };

    // Confetti celebration function
    const fireConfetti = useCallback(() => {
        const duration = 1000;
        const end = Date.now() + duration;
        const colors = ["#ffffff", "#22c55e", "#3b82f6", "#f59e0b", "#ef4444"];

        const frame = () => {
            confetti({
                particleCount: 3,
                angle: 60,
                spread: 55,
                origin: { x: 0, y: 1 },
                colors: colors,
            });

            confetti({
                particleCount: 3,
                angle: 120,
                spread: 55,
                origin: { x: 1, y: 1 },
                colors: colors,
            });

            confetti({
                particleCount: 2,
                angle: 90,
                spread: 100,
                origin: { x: 0.5, y: 1 },
                colors: colors,
            });

            if (Date.now() < end) {
                requestAnimationFrame(frame);
            }
        };

        frame();
    }, []);

    const handlePaymentSuccess = () => {
        setShowStripeModal(false);
        setCheckoutData(null);
        fireConfetti();

        snack.setMessage("Payment successful! Thank you for your purchase.");
        snack.setSeverity("success");
        snack.onOpen();

        setTimeout(() => {
            navigate("/admin/profile?tab=upgrades");
        }, 3500);
    };

    const handlePaymentError = (error: string) => {
        snack.setMessage(error);
        snack.setSeverity("error");
        snack.onOpen();
    };

    const handleCloseStripeModal = () => {
        setShowStripeModal(false);
        setCheckoutData(null);
    };

    const handleClose = () => {
        navigate("/admin/upgrades");
    };

    // Get frequency display text
    const getFrequencyText = (frequency: string) => {
        switch (frequency) {
            case "once":
                return "One-time service";
            case "daily":
                return "Daily service";
            case "weekly":
                return "Weekly service";
            case "monthly":
                return "Monthly service";
            default:
                return `${frequency} service`;
        }
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="service-loading dark-theme">
                <div className="loading-spinner" />
                <p>Loading service...</p>
            </div>
        );
    }

    // Error state
    if (error || !service) {
        return (
            <div className="service-error dark-theme">
                <p className="service-error__message">{error || "Service not found"}</p>
                <button className="service-error__button" onClick={handleClose}>
                    Back to Upgrades
                </button>
            </div>
        );
    }

    const heroImage = service.photoUrl || PLACEHOLDER_IMAGE;
    const priceDisplay = (service.priceCents / 100).toFixed(2);

    return (
        <div className="service-detail dark-theme">
            {/* Close Button */}
            <button className="service-detail__close" onClick={handleClose}>
                <FiX size={22} />
            </button>

            {/* Hero Image */}
            <div className="service-hero">
                <div
                    className="service-hero__image"
                    style={{ backgroundImage: `url(${heroImage})` }}
                />
                <div className="service-hero__overlay" />
            </div>

            {/* Content */}
            <div className="service-content">
                {/* Title */}
                <h1 className="service-content__title">{service.label}</h1>

                {/* Frequency Badge */}
                <span className="service-frequency">{getFrequencyText(service.frequency)}</span>

                {/* Price */}
                <div className="service-price">
                    <span className="service-price__current">
                        ${priceDisplay} {currency}
                    </span>
                </div>

                {/* About Section */}
                <div className="service-content__section">
                    <h2 className="service-content__label">About</h2>
                    <p className="service-content__text">
                        Enhance your stay with this premium service. Our team will ensure everything is prepared to your satisfaction.
                    </p>
                </div>

                {/* Service Code */}
                <div className="service-content__section">
                    <h2 className="service-content__label">Service Code</h2>
                    <p className="service-content__text">{service.code}</p>
                </div>
            </div>

            {/* Sticky Action Bar */}
            <div className="service-action-bar">
                <div className="service-action-bar__container">
                    <div className="service-action-bar__label">Your upgrade:</div>
                    <div className="service-action-bar__selection">
                        <span className="service-action-bar__selection-title">{service.label}</span>
                        <span className="service-action-bar__selection-price">${priceDisplay} {currency}</span>
                    </div>
                    <button
                        className="service-action-bar__button"
                        onClick={handleCheckout}
                        disabled={isCheckingOut}
                    >
                        {isCheckingOut ? (
                            <>
                                <span className="loading-spinner" />
                                Processing...
                            </>
                        ) : (
                            "Purchase"
                        )}
                    </button>
                </div>
            </div>

            {/* Stripe Checkout Modal */}
            {showStripeModal && checkoutData && (
                <div className="stripe-modal">
                    <div className="stripe-modal__backdrop" onClick={handleCloseStripeModal} />
                    <div className="stripe-modal__content">
                        <div className="stripe-modal__header">
                            <h3>Complete Payment</h3>
                            <button className="stripe-modal__close" onClick={handleCloseStripeModal}>
                                <FiX size={18} />
                            </button>
                        </div>
                        <StripeCheckout
                            clientSecret={checkoutData.clientSecret}
                            amount={checkoutData.amount}
                            onSuccess={handlePaymentSuccess}
                            onError={handlePaymentError}
                        />
                    </div>
                </div>
            )}

            {/* Snackbar */}
            <SnackbarComponent
                isOpen={snack.isOpen}
                severity={snack.severity}
                onClose={snack.onClose}
                message={snack.message}
            />
        </div>
    );
}

export default ServiceDetail;
